import { useMemo } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { useModelInsightsKpiTimePeriod } from "@features/mmm/hooks/mmm-url-state";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { Checkbox } from "@shared/checkbox";

import { type ModelMetadataKpiFormula } from "../../explore-budgets/client-setting/types";
import { getColorForIndexOrPlatform } from "../../performance-tracking/tracking-chart/spend-chart/spend-chart-legend/utils";
import { useChannelComparisonModal } from "../hooks/use-channel-comparison-modal";
import { useSelectedChannels } from "../hooks/use-selected-channels";
import { curateHistoricalDataset } from "../model-fit-chart/utils/export-to-csv";

import { type DataColumn } from "./incremental-spend-chart/const";
import { type HistoricalContributionsData } from "./incremental-spend-chart/incremental-spend-chart";
import { KpiMiniChart } from "./kpi-mini-chart";
import { KpiSummaries } from "./kpi-summaries";

interface Props {
  column: DataColumn;
  index: number;
  selectedKpi: ModelMetadataKpiFormula;
  costCurvesAllTimeData: any;
  historicalContributionsData: HistoricalContributionsData;
}

const sumKpiData = (data: any, kpiName: string) =>
  (data as any).reduce(
    (acc: number, { kpiData, promoData }: any) =>
      acc + (kpiData?.[kpiName] ?? 0) + (promoData?.[kpiName] ?? 0),
    0,
  );

export const KpiBreakdown = ({
  column: { spend, breakdowns, label, column_name },
  index,
  selectedKpi,
  costCurvesAllTimeData,
  historicalContributionsData,
}: Props) => {
  const { designElementManager } = useDesignElementManager();
  const {
    selectedChannels,
    selectChannel,
    clearSelectedChannels,
    isMaxSelectableChannelsReached,
  } = useSelectedChannels();
  const [dateRange] = useModelInsightsKpiTimePeriod();

  const { openModalWithChannel } = useChannelComparisonModal();

  const checkboxColor = getColorForIndexOrPlatform(index, breakdowns);

  const renderLabel = () => {
    if (breakdowns.length === 2) {
      return (
        <div className="break-all">
          <div className="text-gray+2">
            {breakdowns[0].value}
            {" / "}
          </div>
          <div className="text-interstellar+2">{breakdowns[1].value}</div>
        </div>
      );
    }

    return <span className="text-interstellar+2  break-all">{label}</span>;
  };

  const rows = useMemo(
    () =>
      curateHistoricalDataset(
        historicalContributionsData[column_name],
        dateRange,
      ),
    [historicalContributionsData, column_name, dateRange],
  );

  const sumOfSpendOnChannel = useMemo(
    () => rows.reduce((acc: number, data: any) => acc + data.spend, 0),
    [rows],
  );

  const sumOfKpiOnChannel = useMemo(
    () => sumKpiData(rows, selectedKpi.kpi_name),
    [rows, selectedKpi.kpi_name],
  );
  const isSelected = selectedChannels.includes(column_name);

  return (
    <motion.div
      className={clsx(
        "border border-v2-secondary rounded-lg p-5 shadow-sm hover:border-gray+1 group/container flex flex-col justify-between",
        {
          "cursor-pointer":
            !isMaxSelectableChannelsReached ||
            selectedChannels.includes(column_name),
        },
      )}
      onClick={(e) => {
        e.stopPropagation();
        selectChannel(column_name)(!isSelected);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        clearSelectedChannels();
        selectChannel(column_name)(true);
        openModalWithChannel(column_name);
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex items-center">
        <Checkbox
          className="mr-2"
          value={isSelected}
          checkboxColor={checkboxColor}
        />
        <span className="flex-1 flex items-center justify-between mr-6">
          <div className="flex items-center space-x-1.5 text-lg font-semibold leading-5">
            {designElementManager
              .getDesignElement(breakdowns[0]?.value || label)
              .getComponent({ size: "100%", className: "w-10 h-10 mr-1" })}
            {renderLabel()}
          </div>
        </span>
        <span
          className="self-start cursor-pointer pl-4 pb-4 group"
          onClick={(e) => {
            e.stopPropagation();
            openModalWithChannel(column_name);
          }}
        >
          <i className="fa-solid fa-up-right-and-down-left-from-center text-cosmic group-hover:animate-jello" />
        </span>
      </div>
      <div className="grid grid-cols-[60%_40%]">
        <KpiSummaries
          sumOfSpendOnChannel={sumOfSpendOnChannel}
          sumOfKpiOnChannel={sumOfKpiOnChannel}
          selectedKpi={selectedKpi}
          spend={spend}
        />
        <KpiMiniChart
          selectedKpi={selectedKpi}
          columnName={column_name}
          historicalContributionsData={historicalContributionsData}
          costCurvesAllTimeData={costCurvesAllTimeData}
        />
      </div>
    </motion.div>
  );
};
